import type { SquadArchetype } from '../simulation/entities/Squad.js';

import {
  CUT_LINK_CHANNEL_SEC,
  CUT_LINK_COOLDOWN_SEC,
  CUT_LINK_DURATION_SEC,
  CUT_LINK_TARGET_RANGE_PX,
} from './cutLink.js';
import {
  DISRUPT_UPLINK_CHANNEL_SEC,
  DISRUPT_UPLINK_COOLDOWN_SEC,
  DISRUPT_UPLINK_DURATION_SEC,
  DISRUPT_UPLINK_FIRE_RATE_MULT,
  DISRUPT_UPLINK_RANGE_MULT,
  DISRUPT_UPLINK_TARGET_RANGE_PX,
} from './disruptUplink.js';
import {
  FORTIFY_RELAY_CHANNEL_SEC,
  FORTIFY_RELAY_COOLDOWN_SEC,
  FORTIFY_RELAY_DURATION_SEC,
  FORTIFY_RELAY_TARGET_RANGE_PX,
  FORTIFY_RELAY_TURRET_FIRE_RATE_MULT,
} from './fortifyRelay.js';
import {
  JAM_RELAY_CHANNEL_SEC,
  JAM_RELAY_COOLDOWN_SEC,
  JAM_RELAY_DURATION_SEC,
  JAM_RELAY_TARGET_RANGE_PX,
} from './jamRelay.js';

// V1 per-archetype abilities. AbilitySystem reads this table to apply the effect
// when a hotkey-press AbilityCommand arrives. Numbers come from
// docs/02_GAME_DESIGN.md §2 (Recon / Assault / Engineer / Shield ability lines)
// and §13 (Recon's long-term disruption set — Cut Link arrives V1.5+ Slice 2).
//
// Cooldowns are in seconds and apply as-is via AbilitySystem to either
// `Squad.abilityCooldown` (Q hotkey) or `Squad.secondaryAbilityCooldown` (E
// hotkey). Effect durations are in seconds; AbilitySystem converts to ticks at
// apply time.
//
// V1 Q abilities (one per archetype):
// 'mark_target'  — Recon. Targeted: paints an enemy entity for 8 s; +10% damage,
//                 visible-through-fog to the marking player.
// 'suppressing_fire' — Assault. Area-on-self: a cone in front of the casting squad,
//                 -30% movement speed for 4 s for enemy squads inside.
// 'deploy_mini_turret' — Engineer. Self-anchored: spawns a 30 s mini-turret at the
//                 squad's position. Anti-infantry. 25 s cooldown.
// 'phase_shield' — Shield. Self-anchored: a 4 s bubble that absorbs 200 damage
//                 worth of incoming damage to allies inside.
//
// V1.5+ Slice 2 secondary E abilities:
// 'cut_link' — Recon. Targeted control_node: 3 s channel at an enemy/neutral
//              relay. If uninterrupted, severs that relay's links for 30 s
//              (the node stops conducting through SignalLinkSystem's BFS so
//              downstream owned relays drop to isolated). Channel cancels on
//              the casting Recon moving, taking damage, or dying. 45 s
//              cooldown — the Recon can either paint a target OR cut a link
//              in any 45-second window. Strategic dynamic per §14: "I cut
//              his mid relay; his forward turret nest goes dark."
//
// V1.5+ Slice 5 secondary E abilities:
// 'jam_relay' — Engineer. Targeted control_node: 2 s channel at an enemy
//              relay. If uninterrupted, jams that relay for 20 s. Distinct
//              from Cut Link: the chain stays intact (BFS still traverses
//              through the jammed node), so downstream relays remain
//              connected and income is unaffected — only the jammed node's
//              vision drops. Engineer is tankier than Recon so it can
//              commit closer (200 px vs Cut Link's 220 px) but the channel
//              is shorter to compensate for the reduced mobility. 35 s
//              cooldown — scalpel-class, spammier than Cut Link's hammer.
//              Strategic dynamic per §16: "I jammed his mid relay; he
//              can't see me crossing the choke."
//
// V1.5+ Slice 6 secondary E ability:
// 'disrupt_uplink' — Assault. Targeted control_node: 2.5 s channel at an
//              enemy/neutral relay within 240 px. If uninterrupted, the
//              relay enters a 25 s disruption window during which forward
//              `mini_turret`s anchored on it (within FORWARD_TURRET_RANGE_PX)
//              fire at 60% rate and 70% range. Distinct from Cut Link AND
//              Jam Relay: the chain stays intact (income/connectivity
//              unaffected), the relay's vision is unaffected, and turrets
//              stay ONLINE — but their forward firepower is degraded for
//              the window. The mechanical novelty is continuous degradation
//              rather than binary on/off (per docs/11_ROADMAP.md Phase 6+
//              row 6). 40 s cooldown — between Cut Link's 45 and Jam
//              Relay's 35. Strategic dynamic per §15: "I disrupted his
//              mid relay; my Assault push survives the turret trade."
//
// V1.5+ Slice 7 secondary E ability:
// 'fortify_relay' — Shield. Targeted control_node: 2.5 s channel at a
//              friendly relay within 200 px. If uninterrupted, the relay
//              enters a 20 s fortify window. The FIRST defensive primitive
//              in the V1.5+ arc — the dual of cut/jam/disrupt. Effects:
//              (1) Incoming cut_link / jam_relay / disrupt_uplink APPLY is
//              a no-op on the fortified relay (the channeling enemy still
//              pays the cooldown — fortify is a counter-window, not a
//              deflection of the channel itself); (2) Forward `mini_turret`s
//              anchored on the relay and owned by the fortifier get +25%
//              fire rate via FORTIFY_RELAY_TURRET_FIRE_RATE_MULT — the
//              mathematical mirror of Disrupt Uplink's 0.6×. 50 s cooldown
//              (longest in the arc — uptime ≤ 40% so the §17 anti-turtling
//              gate has headroom). Strategic dynamic per §15 / §16: "I
//              fortified my forward relay; his Disrupt Uplink fizzled and
//              my turret traded up."

export type AbilityId =
  | 'mark_target'
  | 'suppressing_fire'
  | 'deploy_mini_turret'
  | 'phase_shield'
  | 'cut_link'
  | 'jam_relay'
  | 'disrupt_uplink'
  | 'fortify_relay';

// V1.5+ Slice 2 introduces the second hotkey slot. Squads with a secondary
// ability use 'E'; Q remains the V1 single-ability slot. AbilityCommand carries
// the hotkey discriminator so AbilitySystem decrements the right cooldown.
export type AbilityHotkey = 'Q' | 'E';

export interface AbilityDef {
  id: AbilityId;
  name: string;
  hotkey: AbilityHotkey;
  cooldownSec: number;
  durationSec: number;
  // Whether the ability needs a target on the issued command. Only Mark Target
  // currently needs one; the other three are squad-anchored.
  requiresTarget: boolean;
  // 'mark_target' damage bonus applied multiplicatively by CombatSystem when the
  // attacker's owner is the marker.
  markDamageMult?: number;
  // 'suppressing_fire' multiplier applied in MovementSystem when suppressed.
  suppressionSpeedMult?: number;
  // 'suppressing_fire' cone geometry. Range = forward distance; halfAngleRad = cone
  // half-aperture in radians.
  suppressionRange?: number;
  suppressionHalfAngleRad?: number;
  // 'phase_shield' radius (px) and total damage absorbed before the bubble pops.
  shieldRadius?: number;
  shieldAbsorb?: number;
  // 'deploy_mini_turret' tuning. The mini-turret entity is created via
  // BuildingSystem with hp=miniTurretHp; expires after miniTurretDurationSec or
  // when hp ≤ 0 (whichever first).
  miniTurretHp?: number;
  miniTurretDamagePerShot?: number;
  miniTurretAttackCooldownSec?: number;
  miniTurretRange?: number;
  miniTurretProjectileSpeed?: number;
  miniTurretVision?: number;
  miniTurretDurationSec?: number;
  // 'mark_target' duration is captured in durationSec; range limits how far the
  // marker can reach the target from the casting squad's position.
  markRange?: number;
  // 'cut_link' tuning. The Recon channels for `cutLinkChannelSec` at an
  // enemy/neutral relay within `cutLinkTargetRangePx` of its position; on
  // completion the node's links are severed for `cutLinkDurationSec`. Captured
  // here so HUD/Renderer/bot can read all three from the AbilityDef rather than
  // re-importing the constants.
  cutLinkTargetRangePx?: number;
  cutLinkChannelSec?: number;
  cutLinkDurationSec?: number;
  // 'jam_relay' tuning. Same shape as Cut Link's three knobs (range / channel /
  // effect-window) so HUD/Renderer/bot can read them uniformly. The mechanic
  // differs from Cut Link in WHAT IT DISRUPTS, not in the cast loop.
  jamRelayTargetRangePx?: number;
  jamRelayChannelSec?: number;
  jamRelayDurationSec?: number;
  // 'disrupt_uplink' tuning. Range / channel / effect-window mirror the cut /
  // jam pattern; the analog character is captured by the additional
  // fireRateMult and rangeMult knobs (CombatSystem reads these to scale the
  // turret's cooldown reset and acquisition range while the relay is in a
  // disruption window). Multipliers are applied on top of the base def values
  // — a turret already affected by Bulwark range gets disrupt-multiplied
  // afterward.
  disruptUplinkTargetRangePx?: number;
  disruptUplinkChannelSec?: number;
  disruptUplinkDurationSec?: number;
  disruptUplinkFireRateMult?: number;
  disruptUplinkRangeMult?: number;
  // 'fortify_relay' tuning. Range / channel / effect-window mirror the cut /
  // jam / disrupt pattern; the defensive character is captured by the
  // turretFireRateMult knob (CombatSystem reads it to scale the turret's
  // cooldown reset DOWN — the opposite direction of Disrupt Uplink's mult).
  // Composes with Bulwark range and Disrupt Uplink the same way: the most
  // recent multiplier wins on the same lever, but range and rate are
  // separate levers so a fortified+bulwark turret is faster AND ranged.
  fortifyRelayTargetRangePx?: number;
  fortifyRelayChannelSec?: number;
  fortifyRelayDurationSec?: number;
  fortifyRelayTurretFireRateMult?: number;
}

export const MARK_DAMAGE_MULT = 1.1;
export const SUPPRESSION_SPEED_MULT = 0.7;

export const ABILITY_DEFS: Record<AbilityId, AbilityDef> = {
  mark_target: {
    id: 'mark_target',
    name: 'Mark Target',
    hotkey: 'Q',
    cooldownSec: 30,
    durationSec: 8,
    requiresTarget: true,
    markDamageMult: MARK_DAMAGE_MULT,
    markRange: 600,
  },
  suppressing_fire: {
    id: 'suppressing_fire',
    name: 'Suppressing Fire',
    hotkey: 'Q',
    cooldownSec: 30,
    durationSec: 4,
    requiresTarget: false,
    suppressionSpeedMult: SUPPRESSION_SPEED_MULT,
    // V1 balance pass: range nudged from 320 → 360 so the cone reaches the
    // edge of the Assault firing range (Assault.range=280 plus a member
    // formation footprint). Cone half-angle unchanged.
    suppressionRange: 360,
    suppressionHalfAngleRad: Math.PI / 4,
  },
  deploy_mini_turret: {
    id: 'deploy_mini_turret',
    name: 'Deploy Mini-Turret',
    hotkey: 'Q',
    cooldownSec: 25,
    durationSec: 30,
    requiresTarget: false,
    miniTurretHp: 120,
    miniTurretDamagePerShot: 8,
    miniTurretAttackCooldownSec: 0.5,
    miniTurretRange: 320,
    miniTurretProjectileSpeed: 700,
    miniTurretVision: 6,
    miniTurretDurationSec: 30,
  },
  phase_shield: {
    id: 'phase_shield',
    name: 'Phase Shield',
    hotkey: 'Q',
    cooldownSec: 30,
    durationSec: 4,
    requiresTarget: false,
    // V1 balance pass: radius 140 → 160 so an Assault formation (4 members up
    // to ~18 px from squad center) plus an ally Shield can both fit inside.
    // Absorb pool 200 unchanged — feels right against single-archetype focus.
    shieldRadius: 160,
    shieldAbsorb: 200,
  },
  cut_link: {
    id: 'cut_link',
    name: 'Cut Link',
    hotkey: 'E',
    cooldownSec: CUT_LINK_COOLDOWN_SEC,
    // durationSec captures the channel time — the same field semantics every
    // other AbilityDef uses (the "active window" of the cast). The 30 s
    // post-channel sever lives in cutLinkDurationSec so it's not confused with
    // the channel.
    durationSec: CUT_LINK_CHANNEL_SEC,
    requiresTarget: true,
    cutLinkTargetRangePx: CUT_LINK_TARGET_RANGE_PX,
    cutLinkChannelSec: CUT_LINK_CHANNEL_SEC,
    cutLinkDurationSec: CUT_LINK_DURATION_SEC,
  },
  jam_relay: {
    id: 'jam_relay',
    name: 'Jam Relay',
    hotkey: 'E',
    cooldownSec: JAM_RELAY_COOLDOWN_SEC,
    // Same `durationSec = channel` convention as Cut Link — the 20 s jam
    // window lives in `jamRelayDurationSec` to avoid confusion with the
    // 2 s cast.
    durationSec: JAM_RELAY_CHANNEL_SEC,
    requiresTarget: true,
    jamRelayTargetRangePx: JAM_RELAY_TARGET_RANGE_PX,
    jamRelayChannelSec: JAM_RELAY_CHANNEL_SEC,
    jamRelayDurationSec: JAM_RELAY_DURATION_SEC,
  },
  disrupt_uplink: {
    id: 'disrupt_uplink',
    name: 'Disrupt Uplink',
    hotkey: 'E',
    cooldownSec: DISRUPT_UPLINK_COOLDOWN_SEC,
    // `durationSec = channel` (2.5 s) consistent with cut / jam; the 25 s
    // disruption window lives in disruptUplinkDurationSec.
    durationSec: DISRUPT_UPLINK_CHANNEL_SEC,
    requiresTarget: true,
    disruptUplinkTargetRangePx: DISRUPT_UPLINK_TARGET_RANGE_PX,
    disruptUplinkChannelSec: DISRUPT_UPLINK_CHANNEL_SEC,
    disruptUplinkDurationSec: DISRUPT_UPLINK_DURATION_SEC,
    disruptUplinkFireRateMult: DISRUPT_UPLINK_FIRE_RATE_MULT,
    disruptUplinkRangeMult: DISRUPT_UPLINK_RANGE_MULT,
  },
  fortify_relay: {
    id: 'fortify_relay',
    name: 'Fortify Relay',
    hotkey: 'E',
    cooldownSec: FORTIFY_RELAY_COOLDOWN_SEC,
    // `durationSec = channel` (2.5 s) consistent with cut / jam / disrupt;
    // the 20 s fortify window lives in fortifyRelayDurationSec.
    durationSec: FORTIFY_RELAY_CHANNEL_SEC,
    requiresTarget: true,
    fortifyRelayTargetRangePx: FORTIFY_RELAY_TARGET_RANGE_PX,
    fortifyRelayChannelSec: FORTIFY_RELAY_CHANNEL_SEC,
    fortifyRelayDurationSec: FORTIFY_RELAY_DURATION_SEC,
    fortifyRelayTurretFireRateMult: FORTIFY_RELAY_TURRET_FIRE_RATE_MULT,
  },
};

export const ARCHETYPE_ABILITY: Record<SquadArchetype, AbilityId> = {
  recon: 'mark_target',
  assault: 'suppressing_fire',
  engineer: 'deploy_mini_turret',
  shield: 'phase_shield',
};

// V1.5+ Slice 2 / Slice 5 / Slice 6 / Slice 7 secondary E-slot abilities.
// After Slice 7 every archetype has a primary + secondary — the per-archetype
// ability matrix is complete. Type kept as Partial so existing call-sites
// that null-check `id === undefined` (AbilitySystem.resolveAbilityFor,
// secondaryAbilityForArchetype below) keep typechecking even though the
// table is now total.
export const ARCHETYPE_SECONDARY_ABILITY: Partial<Record<SquadArchetype, AbilityId>> = {
  recon: 'cut_link',
  engineer: 'jam_relay',
  assault: 'disrupt_uplink',
  shield: 'fortify_relay',
};

export function abilityForArchetype(archetype: SquadArchetype): AbilityDef {
  return ABILITY_DEFS[ARCHETYPE_ABILITY[archetype]];
}

export function secondaryAbilityForArchetype(archetype: SquadArchetype): AbilityDef | null {
  const id = ARCHETYPE_SECONDARY_ABILITY[archetype];
  if (id === undefined) return null;
  return ABILITY_DEFS[id];
}
